import type { Facet, IdsFile, IdsSpec, IdsValue } from './idsParse'
import type { ModelSuggestions } from './idsSuggest'

/** Auditar um IDS contra o modelo ANTES de o correr.
 *
 *  Uma validação que dá "100 elementos falham" não distingue um modelo
 *  mal feito de um IDS que pede o que o modelo nunca teve: um pset que
 *  não existe em lado nenhum, uma classificação que ninguém usou, uma
 *  classe que o modelo não tem. Isto olha para o IDS e para o que o
 *  modelo contém e diz, por especificação, o que não pode dar certo e
 *  porquê. Não corre a validação; é uma leitura prévia.
 *
 *  Só se afirma o que se pode afirmar: um valor por padrão (regex) ou
 *  por enumeração parcial fica como aviso, nunca como certeza. Puro,
 *  com testes; a leitura do modelo é do `idsSuggest`. */

export type Gravidade = 'aplica-a-nada' | 'falha-sempre' | 'aviso'

export interface Achado {
  gravidade: Gravidade
  /** Onde: 'aplicabilidade' ou 'requisito'. */
  onde: 'aplicabilidade' | 'requisito' | 'especificacao'
  facet: Facet['type'] | 'esquema'
  mensagem: string
}

export interface AuditoriaDaSpec {
  indice: number
  nome: string
  achados: Achado[]
}

export interface Auditoria {
  specs: AuditoriaDaSpec[]
  /** Quantas especificações não se aplicam a nada, quantas vão falhar
   *  sempre, quantas têm só avisos. */
  aplicamANada: number
  falhamSempre: number
  comAvisos: number
  limpas: number
}

/** Os valores fixos de um IdsValue (simpleValue ou enumeração). Vazio
 *  quando é um padrão ou um intervalo: aí não se sabe. */
export function valoresFixos(v: IdsValue | undefined): string[] {
  if (!v) return []
  if (v.simpleValue !== undefined) return [v.simpleValue]
  if (v.enumeration && v.enumeration.length > 0) return v.enumeration
  return []
}

function temPadrao(v: IdsValue | undefined): boolean {
  return !!v && v.simpleValue === undefined && !(v.enumeration && v.enumeration.length > 0) && v.pattern !== undefined
}

const maiusculas = (s: string) => s.trim().toUpperCase()
const igual = (a: string, b: string) => a.trim().localeCompare(b.trim(), undefined, { sensitivity: 'accent' }) === 0

/** O esquema do modelo cabe na lista da spec? "IFC4X3_ADD2" cabe em
 *  "IFC4X3". Sem lista na spec, cabe sempre. */
export function esquemaCompativel(specVersoes: string[], esquemaDoModelo: string | null): boolean {
  if (specVersoes.length === 0 || !esquemaDoModelo) return true
  const m = maiusculas(esquemaDoModelo)
  return specVersoes.some((v) => m.startsWith(maiusculas(v)) || maiusculas(v).startsWith(m))
}

function auditarFacet(
  f: Facet,
  onde: 'aplicabilidade' | 'requisito',
  modelo: ModelSuggestions,
  entidades: Set<string>,
): Achado[] {
  const fora: Achado[] = []
  // Um requisito proibido ou opcional não falha por o modelo não ter a
  // coisa: é isso mesmo que ele pede, ou aceita.
  const requisitoQueExige = onde === 'requisito' && (f.type === 'unsupported' || !('cardinality' in f) || f.cardinality === 'required' || f.cardinality === undefined)
  const gravidadeDeFalta: Gravidade = onde === 'aplicabilidade' ? 'aplica-a-nada' : 'falha-sempre'
  const conta = onde === 'aplicabilidade' || requisitoQueExige

  switch (f.type) {
    case 'entity': {
      const nomes = valoresFixos(f.name).map(maiusculas)
      if (nomes.length === 0) {
        if (temPadrao(f.name)) fora.push({ gravidade: 'aviso', onde, facet: 'entity', mensagem: 'A classe é dada por um padrão; não se consegue confirmar antes de validar.' })
        break
      }
      const ausentes = nomes.filter((n) => !entidades.has(n))
      if (ausentes.length === nomes.length && conta) {
        fora.push({ gravidade: gravidadeDeFalta, onde, facet: 'entity', mensagem: `O modelo não tem ${ausentes.join(', ')}.` })
      } else if (ausentes.length > 0 && ausentes.length < nomes.length) {
        fora.push({ gravidade: 'aviso', onde, facet: 'entity', mensagem: `O modelo não tem ${ausentes.join(', ')}; só ${nomes.filter((n) => entidades.has(n)).join(', ')} conta.` })
      }
      break
    }
    case 'property': {
      const psets = valoresFixos(f.propertySet)
      const nomes = valoresFixos(f.baseName)
      if (psets.length === 0) {
        if (temPadrao(f.propertySet)) fora.push({ gravidade: 'aviso', onde, facet: 'property', mensagem: 'O property set é dado por um padrão; não se consegue confirmar antes de validar.' })
        break
      }
      const psetsPresentes = psets.filter((p) => modelo.psets.some((m) => igual(m, p)))
      if (psetsPresentes.length === 0) {
        if (conta) fora.push({ gravidade: gravidadeDeFalta, onde, facet: 'property', mensagem: `Nenhum elemento do modelo tem o property set ${psets.join(' ou ')}.` })
        break
      }
      if (nomes.length === 0) break
      const existeProp = (p: string, n: string) => (modelo.propertiesByPset.get(p) ?? modelo.propertiesByPset.get([...modelo.propertiesByPset.keys()].find((k) => igual(k, p)) ?? '') ?? []).some((x) => igual(x, n))
      const semProp = nomes.filter((n) => !psetsPresentes.some((p) => existeProp(p, n)))
      if (semProp.length === nomes.length && conta) {
        fora.push({ gravidade: gravidadeDeFalta, onde, facet: 'property', mensagem: `O property set ${psetsPresentes.join('/')} existe, mas nunca com a propriedade ${nomes.join(' ou ')}.` })
      } else if (semProp.length > 0 && semProp.length < nomes.length) {
        fora.push({ gravidade: 'aviso', onde, facet: 'property', mensagem: `A propriedade ${semProp.join(', ')} não aparece em ${psetsPresentes.join('/')} em nenhum elemento.` })
      }
      break
    }
    case 'classification': {
      const sistemas = valoresFixos(f.system)
      if (sistemas.length === 0) break
      const ausentes = sistemas.filter((s) => !modelo.classificationSystems.some((m) => igual(m, s)))
      if (ausentes.length === sistemas.length && conta) {
        fora.push({ gravidade: gravidadeDeFalta, onde, facet: 'classification', mensagem: `O modelo não tem a classificação ${sistemas.join(' ou ')}.` })
      } else if (ausentes.length > 0 && ausentes.length < sistemas.length) {
        fora.push({ gravidade: 'aviso', onde, facet: 'classification', mensagem: `O modelo não tem a classificação ${ausentes.join(', ')}.` })
      }
      break
    }
    case 'material': {
      const nomes = valoresFixos(f.value)
      if (nomes.length === 0) break
      const ausentes = nomes.filter((n) => !modelo.materials.some((m) => igual(m, n)))
      if (ausentes.length === nomes.length && conta) {
        fora.push({ gravidade: gravidadeDeFalta, onde, facet: 'material', mensagem: `O modelo não tem o material ${nomes.join(' ou ')}.` })
      } else if (ausentes.length > 0 && ausentes.length < nomes.length) {
        fora.push({ gravidade: 'aviso', onde, facet: 'material', mensagem: `O modelo não tem o material ${ausentes.join(', ')}.` })
      }
      break
    }
    case 'partOf': {
      const nomes = f.entity ? valoresFixos(f.entity.name).map(maiusculas) : []
      const ausentes = nomes.filter((n) => !entidades.has(n))
      if (nomes.length > 0 && ausentes.length === nomes.length && conta) {
        fora.push({ gravidade: gravidadeDeFalta, onde, facet: 'partOf', mensagem: `O modelo não tem o contentor ${nomes.join(' ou ')}.` })
      }
      break
    }
    case 'unsupported':
      fora.push({ gravidade: 'aviso', onde, facet: 'unsupported', mensagem: 'Facet que o validador não avalia: fica por confirmar.' })
      break
    case 'attribute':
      // Os atributos existem em qualquer entidade: nada a dizer antes de
      // validar.
      break
  }
  return fora
}

export function auditarSpec(spec: IdsSpec, indice: number, modelo: ModelSuggestions, esquema: string | null): AuditoriaDaSpec {
  const entidades = new Set(modelo.entities.map(maiusculas))
  const achados: Achado[] = []
  if (!esquemaCompativel(spec.ifcVersion, esquema)) {
    achados.push({
      gravidade: 'aplica-a-nada',
      onde: 'especificacao',
      facet: 'esquema',
      mensagem: `A especificação é para ${spec.ifcVersion.join(', ')} e o modelo é ${esquema}.`,
    })
  }
  for (const f of spec.applicability) achados.push(...auditarFacet(f, 'aplicabilidade', modelo, entidades))
  for (const f of spec.requirements) achados.push(...auditarFacet(f, 'requisito', modelo, entidades))
  return { indice, nome: spec.name ?? `Especificação ${indice + 1}`, achados }
}

export function auditarIds(ids: IdsFile, modelo: ModelSuggestions, esquema: string | null): Auditoria {
  const specs = ids.specs.map((s, i) => auditarSpec(s, i, modelo, esquema))
  const pior = (a: AuditoriaDaSpec): Gravidade | null =>
    a.achados.some((x) => x.gravidade === 'aplica-a-nada')
      ? 'aplica-a-nada'
      : a.achados.some((x) => x.gravidade === 'falha-sempre')
        ? 'falha-sempre'
        : a.achados.length > 0
          ? 'aviso'
          : null
  return {
    specs,
    aplicamANada: specs.filter((s) => pior(s) === 'aplica-a-nada').length,
    falhamSempre: specs.filter((s) => pior(s) === 'falha-sempre').length,
    comAvisos: specs.filter((s) => pior(s) === 'aviso').length,
    limpas: specs.filter((s) => pior(s) === null).length,
  }
}

export const NOME_DA_GRAVIDADE: Record<Gravidade, string> = {
  'aplica-a-nada': 'Não se aplica a nada',
  'falha-sempre': 'Vai falhar em todos',
  aviso: 'A confirmar',
}
